import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEvent } from "../context/EventProvider";
import { ApiError } from "../api/client";
import { euros } from "./articles";
import {
  createPayment,
  fetchLastInvoice,
  fetchReturnedInvoice,
  asks,
  badFrenchPostalCode,
  hasAddress,
  knownBill,
  missing,
  paymentReturn,
  requiredFields,
  sentBill,
  type Bill,
} from "./payment";
import {
  REGIMES_KEY,
  chosenRegime,
  offeredRegimes,
  regimeLabelKey,
  regimeName,
} from "./regimes";
import { countryOptions } from "./billCountries";
import { useResume } from "./useArticles";
import { PromoCode } from "./PromoCode";
import { useParticipation } from "./useParticipation";
import { useSessionKey } from "../auth/useSessionKey";
import { sameSession } from "../auth/session";
import { useCompany } from "./useSociete";
import { sendActionEmail } from "../emails/send";
import {
  useLocale,
  useSetting,
  useSettings,
  useT,
  useText,
} from "../settings/context";
import { Button } from "../ui/Button";

/**
 * LE PAIEMENT, À LA CONFIRMATION.
 *
 * ⚠️ POURQUOI ICI ET NULLE PART AILLEURS : voir `funnel/payment.ts`. Le serveur
 * facture ce qui est ENREGISTRÉ, et la commande ne l'est qu'à cet écran.
 *
 * ⚠️ RIEN NE S'AFFICHE QUAND IL N'Y A RIEN À PAYER. Ni bandeau vert, ni « 0,00 € ».
 * L'immense majorité des événements ne vend rien : leur confirmation ne doit pas
 * porter une section de paiement vide.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/**
 * Ce que le formulaire demande, dans l'ordre où il le demande. Le régime décide de ce
 * qui se montre (`asks`) et de ce qui est exigé (`requiredFields`).
 *
 * `labelKey` est le mot du produit ; `label` reste le repli français, servi tel quel
 * tant que le dictionnaire est muet.
 */
const FIELDS: ReadonlyArray<{
  key: keyof Bill;
  labelKey: string;
  label: string;
  autocomplete?: string;
}> = [
  {
    key: "social_reason",
    labelKey: "COMPANY_NAME_OR_LEGAL_NAME",
    label: "Nom ou raison sociale",
    autocomplete: "organization",
  },
  {
    /* Facultatif (#544) : une société hors Europe n'en a pas. Voir `requiredFields`. */
    key: "intra_communautaire",
    labelKey: "EU_VAT_NUMBER",
    label: "N° de TVA intracommunautaire",
  },
  {
    key: "address",
    labelKey: "ADDRESS",
    label: "Adresse",
    autocomplete: "address-line1",
  },
  {
    key: "address_more",
    labelKey: "ADDRESS_MORE",
    label: "Complément d'adresse",
    autocomplete: "address-line2",
  },
  {
    key: "postal_code",
    labelKey: "POSTAL_CODE",
    label: "Code postal",
    autocomplete: "postal-code",
  },
  {
    key: "city",
    labelKey: "CITY",
    label: "Ville",
    autocomplete: "address-level2",
  },
  {
    /* Un code ISO, choisi dans une liste : voir `billCountries.ts`. « country » et
       non « country-name » : le navigateur qui remplit tout seul propose le code. */
    key: "country",
    labelKey: "COUNTRY",
    label: "Pays",
    autocomplete: "country",
  },
];

export function Checkout({
  active,
  example = false,
}: {
  active: boolean;
  /**
   * L'organisateur regarde la situation « Paiement en attente » (`panel/situation.tsx`)
   * sans rien devoir lui-même : le bloc se montre quand même, pour qu'il voie le
   * bouton et la mention qu'il règle. Le montant n'est pas inventé, et le paiement ne
   * part pas.
   */
  example?: boolean;
}) {
  const t = useT();
  const event = useEvent();
  const location = useLocation();
  const { me, recorded } = useParticipation();

  const payLabel = useText("FO_MEETING_V2_PAY_LABEL", "Payer");
  const payNotice = useText("FO_MEETING_V2_PAY_NOTICE");
  /* Les catégories que l'organisateur propose (toutes, tant qu'il n'a rien coché). */
  const regimes = offeredRegimes(useSetting(REGIMES_KEY));
  /* Leur nom, que l'organisateur peut changer (voir `regimeName`). */
  const { value } = useSettings();

  const locale = useLocale();

  const [typed, setBill] = useState<Bill>({});
  /**
   * ⚠️ LE RÉGIME EST RAMENÉ AUX CATÉGORIES PROPOSÉES, ICI, une fois pour tout l'écran :
   * les champs exigés et la facture envoyée lisent ce même `bill`.
   * Une catégorie retirée par l'organisateur (reprise de la dernière facture) tombe,
   * et la seule proposée est choisie d'office (voir `chosenRegime`).
   */
  const { type: _typed, ...rest } = typed;
  const type = chosenRegime(typed.type, regimes);
  const bill: Bill = type === undefined ? rest : { ...rest, type };
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [rate, setRate] = useState<string | null>(null);
  /* Les champs fautifs ne se signalent qu'APRÈS une tentative : souligner en rouge un
     formulaire que personne n'a encore rempli est une réprimande avant la faute. */
  const [check, setChecked] = useState(false);

  /**
   * ⚠️ LE MONTANT EST TTC, QUEL QUE SOIT LE RÉGIME CHOISI (#544) : le serveur facture
   * la TVA à tout le monde, sociétés hors de France comprises (voir `funnel/payment.ts`).
   *
   * ⚠️ AUCUNE SÉLECTION N'EST PASSÉE, ET C'EST LE POINT. Sans liste d'articles, le
   * serveur chiffre ce qui est ENREGISTRÉ — exactement ce qu'on veut ici, puisque la
   * commande vient d'être écrite par la confirmation. Lui passer ce que le navigateur
   * croit savoir ferait facturer une sélection tenue en cache, qui peut dater d'avant
   * l'écriture.
   *
   * Effet de bord heureux : plus besoin du catalogue d'articles pour dresser cette
   * liste, donc une requête de moins sur chaque confirmation.
   */
  const { resume } = useResume(null, active);

  /**
   * LES COORDONNÉES DÉJÀ DONNÉES.
   *
   * ⚠️ DEMANDÉES SEULEMENT S'IL Y A QUELQUE CHOSE À PAYER : sinon c'est une requête de
   * plus sur chaque confirmation de chaque événement, pour un formulaire que personne
   * n'ouvrira.
   */
  const due = active && !!event && resume?.toPay === true;
  const last = useQuery({
    queryKey: ["facture-derniere", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchLastInvoice(event!.domainId, event!.eventId, signal),
    enabled: due,
    staleTime: Infinity,
    retry: false,
  });
  /* Pour une première facture : la société représentée ici (voir `knownBill`). Le même
     cache que l'étape société et le récapitulatif. */
  const representing = typeof recorded?.employeeId === "number";
  const company = useCompany(due && representing);

  /* Reprises UNE fois, et seulement dans les cases encore vides : recopier à chaque
     rendu écraserait ce que le payeur est en train de corriger.

     ⚠️ ET UNE FOIS TOUTES LES SOURCES CONNUES. Remplie d'après le profil avant que la
     société ne réponde, la facture ne pourrait plus prendre son adresse : les cases ne
     sont plus vides. Une dernière facture avec adresse, elle, suffit seule. */
  const filled = useRef(false);
  useEffect(() => {
    if (filled.current || !last.isFetched) return;
    const previous = last.data ?? {};
    if (
      !hasAddress(previous) &&
      (!me || !recorded || (representing && !company.fetched))
    ) {
      return;
    }
    filled.current = true;
    const known = knownBill(previous, me, company.attached);
    setBill((before) => ({ ...known, ...before }));
  }, [
    last.isFetched,
    last.data,
    me,
    recorded,
    representing,
    company.fetched,
    company.attached,
  ]);

  const session = useSessionKey();

  /**
   * ═══ À QUI EST CE RETOUR DE STRIPE ? (bug 8) ═══
   *
   * A part payer chez Stripe ; pendant ce temps un autre onglet ouvre le compte de B (le
   * jeton est commun aux onglets). Stripe ramène A sur `?invoice=<id>&status=success`, mais
   * le chargement neuf se fait sous le jeton de B : l'adresse seule faisait dire « Votre
   * paiement a bien été reçu » à B et lui envoyer l'email « Achat confirmé ». Aucune trace
   * locale ne peut trancher : la mémoire de la page repart vide au retour, et
   * `sessionStorage` est celui de l'onglet, pas d'une personne.
   *
   * D'où la question au serveur, sous la session courante : la facture revenue est-elle
   * la dernière de la personne connectée, et payée ? Tant qu'il n'a pas répondu oui, ni
   * bandeau ni email. La clé porte la session : un changement de compte pendant que
   * l'écran est ouvert repose la question sous le nouveau jeton. Le paiement d'A, lui, est
   * déjà enregistré par le serveur (`confirmInvoice`) avant même ce retour.
   */
  const returned = paymentReturn(location.search);
  const proof = useQuery({
    queryKey: ["paiement-retour", event?.domainId, event?.eventId, session, returned],
    queryFn: ({ signal }) =>
      fetchReturnedInvoice(event!.domainId, event!.eventId, signal),
    enabled: active && !!event && returned !== null,
    staleTime: Infinity,
    retry: false,
  });
  const paid =
    returned !== null &&
    typeof me?.id === "number" &&
    proof.data?.id === returned &&
    proof.data.userId === me.id &&
    proof.data.isPaid;

  /**
   * ═══ L'EMAIL APRÈS PAIEMENT (action 8), AU RETOUR DE STRIPE ═══
   *
   * Comme la V1 (`Confirmation/index.jsx`, `query.get('status') === 'success'`) : c'est le
   * retour qui déclenche, une fois par paiement. Le paramètre survit à un rechargement,
   * d'où la trace en `sessionStorage` : sans elle, chaque F5 renverrait l'email.
   */
  useEffect(() => {
    if (!paid || !event || typeof me?.id !== "number") return;
    /* ⚠️ LA PREUVE A ÉTÉ DONNÉE SOUS `session` : un autre compte ouvert depuis n'en hérite pas (bug 8). */
    if (!sameSession(session)) return;
    const mark = `paid-emailed:${event.eventId}:${me.id}:${returned}`;
    try {
      if (sessionStorage.getItem(mark)) return;
      sessionStorage.setItem(mark, "1");
    } catch {
      /* stockage indisponible : on envoie quand même, une fois par montage */
    }
    void sendActionEmail(
      event.domainId,
      event.eventId,
      "paid",
      { userId: me.id },
    ).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paid, event?.eventId, me?.id, session]);

  /**
   * ⚠️ LE RETOUR DE STRIPE EST UNE FÉLICITATION, PAS UNE QUITTANCE. Le bandeau se montre
   * sur la facture revenue (voir `paid`), mais ce qui décide de la suite reste
   * `resume.toPay`, relu auprès du serveur. Si les deux se contredisent — facture payée et solde
   * toujours dû — c'est le serveur qui a raison, et le bouton reste offert : la
   * confirmation de Stripe peut arriver avec un instant de retard, et quelqu'un qui a
   * réellement payé ne verra qu'un écran qui n'a pas encore appris la nouvelle.
   */
  if (!active) return null;
  const sample = example && resume?.toPay !== true;
  if (!paid && !resume?.toPay && !sample) return null;

  const incomplete = missing(bill, regimes);

  /**
   * LE REPROCHE ÉCRIT SOUS UN CHAMP. Celui du code postal (bug #505) attend une
   * tentative, comme le marquage des champs : « cinq chiffres » sous le premier
   * chiffre tapé serait une réprimande avant la faute.
   */
  const fieldError = (key: keyof Bill): string | null => {
    if (key === "postal_code" && check && badFrenchPostalCode(bill)) {
      return t(
        "POSTAL_CODE_FRENCH_FIVE_DIGITS",
        "Un code postal français compte cinq chiffres.",
      );
    }
    return null;
  };

  const pay = async () => {
    /* Un aperçu ne paie rien : aucun montant n'est dû, et la route le refuserait. */
    if (sample) return;
    setChecked(true);
    if (incomplete.length > 0 || sending || !event) return;
    setSending(true);
    setRate(null);
    try {
      const url = await createPayment(
        event.domainId,
        event.eventId,
        sentBill(bill),
        // Origine + chemin, sans chaîne de requête : le serveur y recolle son propre
        // `?status=success` au retour (voir `funnel/payment.ts`).
        `${window.location.origin}${window.location.pathname}`,
      );
      // On QUITTE le site : pas de navigation React Router ici, Stripe héberge sa
      // propre page.
      window.location.href = url;
    } catch (error) {
      /* Les deux refus légitimes de la route méritent mieux qu'« une erreur est
         survenue » : l'un se règle au back office, l'autre veut dire qu'il n'y a rien
         à payer. */
      const message =
        error instanceof ApiError && error.status === 400
          ? /FO PAY/i.test(error.message)
            ? t(
                "PAYMENT_NOT_CONFIGURED",
                "Le paiement en ligne n'est pas configuré sur cet événement. Prévenez l'organisateur.",
              )
            : /too small/i.test(error.message)
              ? t("NOTHING_LEFT_TO_PAY", "Il n'y a plus rien à régler.")
              : error.message
          : t(
              "PAYMENT_NOT_OPENED",
              "Le paiement n'a pas pu être ouvert. Réessayez dans un instant.",
            );
      setRate(message);
      setSending(false);
    }
  };

  const fields = FIELDS.filter((c) => asks(c.key, bill.type));
  const required = requiredFields(bill.type);
  const countries = countryOptions(locale);

  /**
   * CE QU'ON DOIT, TTC — calculé UNE fois, pour les DEUX endroits qui l'écrivent.
   *
   * ⚠️ LE PLANCHER À ZÉRO N'ÉTAIT POSÉ QUE SUR L'UN DES DEUX. Un trop-perçu rend un
   * total négatif (le serveur le donne tel quel, voir `routes/Articles.tsx`) : le bloc
   * du haut annonçait « 0,00 € » pendant que le bouton, dix lignes plus bas, proposait
   * de « Payer −431,00 € ». Deux nombres pour la même dette, dont un impossible — et sur
   * l'écran qui engage de l'argent.
   *
   * ⚠️ `resume` PEUT MANQUER ICI : au retour de Stripe le bandeau s'affiche dès la facture
   * reconnue, avant que le solde relu soit revenu. Les blocs qui écrivent un
   * montant sont tous derrière `resume?.aPayer`, mais le calcul, lui, se fait avant eux.
   */
  const du = Math.max(0, resume?.remainingInclTax ?? 0);

  return (
    <div className="pay" data-cfg="confirmationPay">
      {paid && (
        <p className="pay__ok" role="status">
          <b>{t("PAYMENT_RECEIVED", "Votre paiement a bien été reçu.")}</b>
          {resume?.toPay && (
            /* Le cas où les deux sources se contredisent — dit plutôt que masqué : un
               solde qui reparaît sans explication après un paiement est ce qui fait
               payer deux fois. */
            <small>
              {t(
                "AMOUNT_MAY_BE_STALE",
                "Le montant ci-dessous n'a peut-être pas encore été mis à jour. Rechargez la page dans quelques instants avant de régler à nouveau.",
              )}
            </small>
          )}
        </p>
      )}

      {(resume?.toPay || sample) && (
        <>
          <div className="pay__due">
            <span>{t("LEFT_TO_PAY", "Reste à régler")}</span>
            {sample ? (
              /* Pas de montant d'exemple : un chiffre inventé se relit comme un vrai.
                 En français seulement : seul l'organisateur voit cet aperçu. */
              <b className="pay__sample">montant de la commande</b>
            ) : (
              /* La mention fiscale passe par les mots du produit, comme le total de la
                 commande juste au-dessus (`Recap.tsx`) : écrite en dur, elle restait
                 « TTC » sur le site anglais (bug #340). */
              <b>{t("AMOUNT_INCL_TAX", "$1 TTC", [euros(du, locale)])}</b>
            )}
          </div>

          {/* Juste sous le montant qu'il réduit, avant de payer : là où la V1 le
              posait (`resume/Code.jsx`, au-dessus de son bouton). Pas dans l'aperçu de
              l'organisateur, qui n'a rien à réduire. */}
          {!sample && <PromoCode resume={resume} />}

          {!open && (
            <Button type="button" onClick={() => setOpen(true)}>
              {payLabel}
            </Button>
          )}

          {open && (
            <div className="form pay__form">
              <div className="ttl">
                <h3>{t("BILLING_DETAILS", "Coordonnées de facturation")}</h3>
                <p>
                  {t(
                    "SHOWN_ON_YOUR_INVOICE",
                    "Elles figureront sur votre facture.",
                  )}
                </p>
              </div>

              <div className="f">
                <label htmlFor="pay-type">
                  {t("YOU_ARE_PAYING_AS", "Vous réglez en tant que")} <i>*</i>
                </label>
                <select
                  id="pay-type"
                  value={bill.type ?? ""}
                  aria-invalid={check && incomplete.includes("type")}
                  /* ⚠️ L'OPTION VIDE RETIRE LE RÉGIME, elle n'en pose pas un qui vaut
                     zéro. `Number("")` rend `0`, un type qu'aucune des six branches du
                     serveur ne connaît — et que `manquants` refuse désormais, ce qui
                     rendrait le formulaire impossible à valider si on le laissait
                     s'écrire ici. */
                  onChange={(e) =>
                    setBill(({ type: _, ...before }) =>
                      e.target.value === ""
                        ? before
                        : { ...before, type: Number(e.target.value) },
                    )
                  }
                >
                  {/* Une seule catégorie : elle est déjà choisie, et « Choisissez »
                      ne ferait que la reprendre aussitôt. */}
                  {regimes.length > 1 && (
                    <option value="">{t("CHOOSE_ONE", "Choisissez")}</option>
                  )}
                  {regimes.map((r) => (
                    <option key={r.type} value={r.type}>
                      {regimeName(r, value(regimeLabelKey(r.type)), t)}
                    </option>
                  ))}
                </select>
              </div>

              {fields.map((field) => (
                <div className="f" key={field.key}>
                  <label htmlFor={`pay-${field.key}`}>
                    {t(field.labelKey, field.label)}{" "}
                    {required.includes(field.key) && <i>*</i>}
                  </label>
                  {field.key === "country" ? (
                    <select
                      id="pay-country"
                      autoComplete={field.autocomplete}
                      value={bill.country ?? ""}
                      aria-invalid={check && incomplete.includes("country")}
                      /* L'option vide retire le pays, comme celle du régime : il est
                         facultatif pour un particulier, qui doit pouvoir revenir sur
                         son choix. */
                      onChange={(e) =>
                        setBill(({ country: _, ...before }) =>
                          e.target.value === ""
                            ? before
                            : { ...before, country: e.target.value },
                        )
                      }
                    >
                      <option value="">{t("CHOOSE_ONE", "Choisissez")}</option>
                      {countries.first.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                      <option disabled value="|">
                        ──────────
                      </option>
                      {countries.others.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`pay-${field.key}`}
                      type="text"
                      {...(field.autocomplete
                        ? { autoComplete: field.autocomplete }
                        : {})}
                      value={(bill[field.key] as string | undefined) ?? ""}
                      aria-invalid={check && incomplete.includes(field.key)}
                      {...(fieldError(field.key)
                        ? { "aria-describedby": `pay-${field.key}-err` }
                        : {})}
                      onChange={(e) =>
                        setBill((before) => ({
                          ...before,
                          [field.key]: e.target.value,
                        }))
                      }
                    />
                  )}
                  {fieldError(field.key) && (
                    <p
                      className="f__err"
                      id={`pay-${field.key}-err`}
                      role="alert"
                    >
                      {fieldError(field.key)}
                    </p>
                  )}
                </div>
              ))}

              {check && incomplete.length > 0 && (
                <p className="loginerr" role="alert">
                  {t(
                    "FILL_MARKED_FIELDS",
                    "Complétez les champs marqués pour continuer.",
                  )}
                </p>
              )}

              {rate && (
                <p className="loginerr" role="alert">
                  {rate}
                </p>
              )}

              {/* ⚠️ ON DIT QU'ON S'EN VA. Un bouton « Payer » qui remplace la page par
                  un site tiers, sans prévenir, ressemble à une redirection subie —
                  et c'est exactement la forme qu'ont les pages d'hameçonnage. */}
              <p className="hint">
                {t(
                  "PAYMENT_ON_STRIPE",
                  "Le paiement se fait sur la page sécurisée de Stripe. Vous reviendrez ici ensuite.",
                )}
              </p>

              <div className="actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setOpen(false)}
                >
                  {t("CANCEL", "Annuler")}
                </Button>
                <Button
                  type="button"
                  loading={sending}
                  onClick={() => void pay()}
                >
                  {payLabel} {euros(du, locale)}
                </Button>
              </div>
            </div>
          )}

          {payNotice.trim() !== "" && (
            <p className="hint" data-cfg="confirmationPay">
              {payNotice}
            </p>
          )}
        </>
      )}
    </div>
  );
}
